import React from "react";
import { Circle } from "rc-progress";

export type LoanDashboardProgressProps = {
  value: number;
  size?: "small" | "large";
  label?: string;
  showAmount?: boolean;
  amountContent?: React.ReactNode;
  labelPosition?: "top" | "bottom";
  invertFill?: boolean;
};

const ValueBlock: React.FC<{
  value: number;
  size: "small" | "large";
  showAmount: boolean;
  amountContent?: React.ReactNode;
}> = ({ value, size, showAmount, amountContent }) => (
  <div
    className={`cx-grow cx-text-text-primary cx-font-bold cx-text-center ${
      size === "small" ? "cx-text-sm" : "cx-text-xl"
    } ${
      showAmount ? "cx-flex cx-gap-1 cx-items-baseline cx-justify-center" : ""
    }`}
  >
    {showAmount ? amountContent ?? `${value}` : `${value}%`}
  </div>
);

const LabelBlock: React.FC<{
  label: string;
  size: "small" | "large";
}> = ({ label, size }) => (
  <div
    className={`cx-grow cx-text-[#666] cx-text-center ${
      size === "small" ? "cx-font-medium" : "cx-font-medium"
    } ${size === "small" ? "cx-text-xs" : "cx-text-base"}`}
  >
    {label}
  </div>
);

export const LoanDashboardProgress: React.FC<LoanDashboardProgressProps> = ({
  value,
  size = "small",
  label = "Complete",
  showAmount = false,
  amountContent,
  labelPosition = "bottom",
  invertFill = false,
}) => {
  const percent = invertFill ? 100 - value : value;
  return (
    <div
      className={`cx-flex cx-justify-center cx-mx-auto ${
        size === "small" ? "cx-max-w-[220px]" : "cx-max-w-[300px]"
      }`}
    >
      <div
        className={`cx-px-4 cx-overflow-clip cx-relative ${
          size === "small" ? "cx-w-[170px]" : "cx-w-[351px]"
        } ${size === "small" ? "cx-h-[58px]" : "cx-h-[136px]"}`}
      >
        <Circle
          percent={percent}
          strokeWidth={size === "small" ? 4 : 9}
          strokeLinecap="round"
          trailWidth={size === "small" ? 4 : 9}
          gapDegree={size === "small" ? 220 : 180}
          strokeColor={{ "0%": "#0064FF", "100%": "#0064FF" }}
        />
        <div
          className={`cx-flex cx-flex-col cx-items-center cx-justify-center cx-absolute cx-top-0 cx-left-0 cx-right-0 ${
            size === "small" ? "cx-pt-3" : "cx-pt-16"
          }`}
        >
          {labelPosition === "top" ? (
            <>
              <LabelBlock label={label} size={size} />
              <ValueBlock
                value={value}
                size={size}
                showAmount={showAmount}
                amountContent={amountContent}
              />
            </>
          ) : (
            <>
              <ValueBlock
                value={value}
                size={size}
                showAmount={showAmount}
                amountContent={amountContent}
              />
              <LabelBlock label={label} size={size} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
